import { useEffect, useRef, useState } from 'react'
import { useMediaSource } from '../../provider/mediaSource'
import { SourceType } from '../../const/SourceType'
import styles from './style'
import { usePlayer } from '../../provider/playerSource'
import { useWebsocket } from '../../provider/websocket'
import { useScreenShare } from '../../provider/screenShare'
import Hls from 'hls.js'
import { isMobile } from '../../const/DeviceInfo'
import { getMseCodecString } from '../../utils/mseCodec'

const isIOS = /(iPad|iPhone|iPod)/g.test(navigator.userAgent)

function AVPlayer() {
  const { videoSource } = usePlayer()
  const {
    mediaSource,
    mediaSourceUrl,
    createMediaSource,
    resetMediaSource,
    isSourceOpen,
    bufferReady,
  } = useMediaSource()
  const { webSocket, websocketOpen } = useWebsocket()
  const { remoteShare, joinShare, leaveShare } = useScreenShare()

  const [currentVideoSource, setCurrentVideoSource] = useState(videoSource)

  const bufferTime = 2000
  const videoRef = useRef<HTMLVideoElement>(null)
  const hlsRef = useRef<Hls | null>(null)
  const hlsVideoRef = useRef<HTMLVideoElement>(null)
  const webrtcVideoRef = useRef<HTMLVideoElement>(null)

  const [enabledControls, setEnabledControls] = useState(true)

  // Tracks which websocket channel the current SourceBuffer was opened for,
  // so a channel switch (even without leaving SourceType.WEBSOCKET in
  // between) re-derives the codec and opens a fresh buffer for it.
  const lastWebsocketInstanceRef = useRef<string | null>(null)

  useEffect(() => {
    if (hlsVideoRef.current?.canPlayType('application/vnd.apple.mpegurl') && videoSource) {
      hlsVideoRef.current.src = videoSource.url
    } else if (Hls.isSupported()) {
      const hls = new Hls()
      hlsRef.current = hls

      return () => {
        hls.destroy()
      }
    }
  }, [hlsVideoRef, videoSource])

  useEffect(() => {
    if (videoRef.current && !isMobile) {
      videoRef.current.volume = 0.2

      videoRef.current?.addEventListener('canplay', () => {
        setEnabledControls(true)
      })
    }
    if (hlsVideoRef.current && !isMobile) {
      hlsVideoRef.current.volume = 0.2
    }
  }, [videoSource])

  useEffect(() => {
    if (!isIOS) {
      console.log('CREATING MEDIA SOURCE')
      createMediaSource()
    }
  }, [])

  useEffect(() => {
    if (isIOS || !websocketOpen) return

    if (videoSource?.type === SourceType.WEBSOCKET) {
      const instanceName = videoSource.url

      if (lastWebsocketInstanceRef.current !== instanceName) {
        lastWebsocketInstanceRef.current = instanceName
        resetMediaSource(getMseCodecString(videoSource.videoCodec, videoSource.audioCodec))
        return
      }

      if (!isSourceOpen) return
      setTimeout(() => {
        console.log('Start')
        webSocket.send('start:' + instanceName)
      }, 1000)
    } else if (videoSource?.type === SourceType.HLS) {
      hlsRef.current?.attachMedia(hlsVideoRef.current as HTMLMediaElement)
      hlsRef.current?.loadSource(videoSource.url)
    }
  }, [videoSource, webSocket, websocketOpen, isSourceOpen])

  useEffect(() => {
    if (websocketOpen && videoSource?.type === SourceType.WEBRTC) {
      joinShare(videoSource.url)
    }
  }, [videoSource, websocketOpen, joinShare])

  useEffect(() => {
    if (webrtcVideoRef.current) {
      webrtcVideoRef.current.srcObject =
        videoSource?.type === SourceType.WEBRTC && remoteShare?.shareId === videoSource.url
          ? remoteShare.stream
          : null
    }
  }, [videoSource, remoteShare])

  // <video autoPlay> only attempts to play once, right when the element is
  // created — at that point the SourceBuffer is still empty, so the attempt
  // does nothing and the browser never retries on its own once data shows
  // up. Trigger it explicitly once there's actually something to play.
  useEffect(() => {
    if (videoSource?.type !== SourceType.WEBSOCKET || !bufferReady) return

    videoRef.current?.play().catch(err => {
      console.error('Autoplay failed:', err)
    })
  }, [videoSource, bufferReady])

  useEffect(() => {
    if (
      currentVideoSource?.type === SourceType.WEBSOCKET &&
      videoSource?.type !== SourceType.WEBSOCKET &&
      !isIOS
    ) {
      const instanceName = currentVideoSource.url
      console.log('STOP SUBSCRIPTION')
      console.log('stop:' + instanceName)

      resetMediaSource()
      webSocket.send('stop:' + instanceName)
    } else if (
      currentVideoSource?.type === SourceType.HLS &&
      videoSource?.type !== SourceType.HLS
    ) {
      hlsRef.current?.stopLoad()
    } else if (
      currentVideoSource?.type === SourceType.WEBRTC &&
      videoSource?.type !== SourceType.WEBRTC
    ) {
      leaveShare(currentVideoSource.url)
    }
    setCurrentVideoSource(videoSource)
  }, [currentVideoSource, resetMediaSource, videoSource, webSocket, leaveShare])

  if ((!isIOS ? !mediaSource.current : false) || !videoSource)
    return <div style={styles.placeholder} />

  if (videoSource.type === SourceType.WEBSOCKET) {
    return (
      <video
        ref={videoRef}
        controls
        src={mediaSourceUrl ?? undefined}
        playsInline={false}
        onPlay={() => {
          console.log('VIDEO PLAY')
          if (videoRef.current && videoRef.current.buffered.length) {
            videoRef.current.currentTime = videoRef.current.buffered.end(0) - bufferTime / 1000
          }
        }}
        onPause={() => {
          console.log('VIDEO PAUSE')
        }}
        style={styles.video}
      />
    )
  }

  if (videoSource.type === SourceType.FILE) {
    return (
      <video
        ref={videoRef}
        controls
        autoPlay
        src={videoSource.url}
        style={styles.video}
      />
    )
  }

  if (videoSource.type === SourceType.HLS) {
    return (
      <video
        ref={hlsVideoRef}
        controls
        style={styles.video}
        autoPlay
      />
    )
  }

  if (videoSource.type === SourceType.WEBRTC) {
    return (
      <video
        ref={webrtcVideoRef}
        controls
        autoPlay
        style={styles.video}
      />
    )
  }

  return null
}

export default AVPlayer
